/**
 * ThemeToggle Component (CAND-14: Dark / Light Theme Toggle)
 *
 * Responsibilities:
 * 1. Provides a one-click accessible toggle between light and dark themes.
 * 2. Visual icon indication (Sun for dark mode, Moon for light mode).
 * 3. Proper ARIA attributes for screen readers.
 * 4. Smooth visual transition.
 */

import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

export interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  size = 'sm',
}) => {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const iconSize = size === 'sm' ? 16 : 18;
  const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';

  return (
    <button
      type="button"
      className={`theme-toggle-btn ${className}`.trim()}
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      data-testid="theme-toggle-button"
    >
      {isDark ? (
        <Sun size={iconSize} className="theme-toggle-icon theme-toggle-sun" />
      ) : (
        <Moon size={iconSize} className="theme-toggle-icon theme-toggle-moon" />
      )}
      <span className="sr-only">{label}</span>
    </button>
  );
};
